import React, { FC, useContext, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { BasketPxDetailsProps } from '@components/BasketPxDetails';
import { faqsTemplateId } from '@components/FAQs';
import PartExchangeWidget from '@components/PartExchange/PartExchangeWidget';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import { Link as LinkV2 } from '@components/v2/link';
import Link from '@components/Link';
import DealerPartExchange from '@components/DealerPartExchange';
import { usePersonalizedOffer } from '@hooks/usePersonalizedOffer';
import { useUserDuck } from '@hooks/useUserDuck';
import Button from '@components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { IconTemplate } from '@components/Icon/IconTemplate';
import {
    PX_STATUSES_APPROVED,
    PX_STATUSES_CONTINUE,
    PX_STATUSES_ERROR,
    PX_STATUSES_IN_PROGRESS,
    PX_STATUSES_WAITING,
} from '../../partExchange/constants/config';
import { deletePartExchange, syncPartExchange } from '../../partExchange/service/ApiService';
import { Redux } from '../../redux/redux.interface';
import ConfiguratorDuck from '../../redux/configurator/configurator.duck';
import { FEATURES_LIST, FeatureSwitchContext } from '../../context/featureSwitchApp';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';
import { isBrandAC, ModalVersion, PxVersion } from '../../constants/main';
import UIDuck from '../../redux/commonDucks/ui.duck';
import { processMopData } from '../../partExchange/utility/general';
import { openC2SWidget } from '@utils/Car.utils';

const BasketPxDetailsTemplate: FC<BasketPxDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();

    const dispatch = useDispatch();

    const context = useContext(FeatureSwitchContext);

    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();

    const { isUserLoggedInAsDealer } = useUserDuck();

    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);

    const { isPersonalizedDeal } = usePersonalizedOffer();

    const { hasPersonalizedPx } = usePersonalizedOffer();

    const [isOpen, setIsOpen] = useState(true);
    const { currentDeal, carDetails } = useCarDetailsDuck();
    const scrollToFAQs = () => {
        const element = document.getElementById(faqsTemplateId);

        return element.scrollIntoView();
    };

    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);

    const showC2SButton =
        pxVersion === PxVersion.v2 &&
        autobizPxState?.externalReferenceId &&
        PX_STATUSES_IN_PROGRESS.includes(autobizPxState?.statusId);
    const showWaitingStatus =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_WAITING.includes(autobizPxState.statusId) : false;
    const showApprovedStatus =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_APPROVED.includes(autobizPxState.statusId) : false;
    const showErrorStatus =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_ERROR.includes(autobizPxState.statusId) : false;
    const showContinueButton =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_CONTINUE.includes(autobizPxState?.statusId) : false;

    return (
        <div className={className} id="px">
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t(
                                    `basket.px.title${isUserLoggedInAsDealer || hasPersonalizedPx ? '.dealer' : ''}`
                                ).toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="description">
                    <span
                        dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                            `basket.px.description${isUserLoggedInAsDealer ? '.dealer' : ''}${
                                isPersonalizedDeal ? '.personalizedOffer' : ''
                            }`
                        )}
                    />
                    <LinkV2
                        withParent={false}
                        isUnderline
                        txt={t(
                            `basket.px.description.text${isUserLoggedInAsDealer ? '.dealer' : ''}${
                                isPersonalizedDeal ? '.personalizedOffer' : ''
                            }`
                        )}
                        onClick={scrollToFAQs}
                    >
                        <button className="link" onClick={scrollToFAQs}>
                            {t(
                                `basket.px.description.text${isUserLoggedInAsDealer ? '.dealer' : ''}${
                                    isPersonalizedDeal ? '.personalizedOffer' : ''
                                }`
                            )}
                        </button>
                    </LinkV2>
                </div>
                {hasPersonalizedPx || isUserLoggedInAsDealer ? (
                    <DealerPartExchange />
                ) : (
                    <PartExchangeWidget selectionMode car={carDetails} mopId={currentDeal?.mopId} />
                )}
                {showContinueButton && (
                    <div className="continue">
                        <p className="continueLabel">{t('basket.px.estimation.continue.label')}</p>
                        <p className="continueDescription">{t('basket.px.estimation.continue.description')}</p>
                        <Button
                            secondary
                            onClick={() => {
                                const element = document.getElementById('widgetView');
                                if (element) {
                                    element.click();
                                }
                            }}
                            withoutBackground={isBrandAC}
                            centerMode
                            marginTop={25}
                        >
                            {t('basket.px.estimation.continue.button')}
                        </Button>
                    </div>
                )}
                {showC2SButton && (
                    <div className="status">
                        <IconTemplate name={Icons.PxEdit} className="statusIcon" />
                        <p className="statusLabel">{t('basket.px.trade.edit.label')}</p>
                        <p className="statusDescription">{t('basket.px.trade.edit.description')}</p>
                        <Button
                            secondary
                            centerMode
                            marginTop={25}
                            withoutBackground={isBrandAC}
                            onClick={() =>
                                openC2SWidget(autobizPxState?.externalReferenceId, dispatch, currentDeal?.token)
                            }
                        >
                            {t('basket.px.trade.edit.button')}
                        </Button>
                    </div>
                )}
                {showErrorStatus && (
                    <div className="status">
                        <IconTemplate name={Icons.PxError} className="statusIcon" />
                        <p className="statusLabel">{t('basket.px.trade.error.label')}</p>
                    </div>
                )}
                {showWaitingStatus && (
                    <div className="status">
                        <IconTemplate name={Icons.PxWaiting} className="statusIcon" />
                        <p className="statusLabel">{t('basket.px.trade.waiting.label')}</p>
                        <p className="statusDescription">{t('basket.px.trade.waiting.description')}</p>
                    </div>
                )}
                {showApprovedStatus && (
                    <div className="status">
                        <IconTemplate name={Icons.PxSuccess} className="statusIcon" />
                        <p className="statusLabel">{t('basket.px.trade.success.label')}</p>
                        <p className="statusDescription">{t('basket.px.trade.success.description')}</p>
                        <button
                            className="trash"
                            onClick={() => {
                                dispatch(
                                    UIDuck.openModal({
                                        data: {
                                            onConfirmClick: () =>
                                                deletePartExchange(null, null, currentDeal?.partExchangeId),
                                        },
                                        modalType: UIDuck.MODAL_TYPES.REMOVE_PX_CONFIRMATION,
                                        modalVersion: ModalVersion.v2,
                                        modalProperties: {
                                            centerModeOnDesktop: true,
                                        },
                                    })
                                );
                            }}
                        >
                            <IconTemplate name={Icons.Trash} />
                            <span>{t('basket.px.trade.success.button')}</span>
                        </button>
                    </div>
                )}
            </Collapsible>
        </div>
    );
};

export default BasketPxDetailsTemplate;
